import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { createActionReporter } from "../../../diagnostics";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded } from "stores/sessionGuard";

export type DeliveryConnectionSortKey = "name" | "type" | "target";

/** All columns are text — every sort starts A→Z. */
const DESC_FIRST: ReadonlySet<DeliveryConnectionSortKey> = new Set();

export interface DeliveryConnection {
  id: string;
  name: string;
  type: string;
  baseUrl?: string | null;
  owner?: string | null;
  ownerType?: string | null;
  projectNumber?: number | null;
  authMode?: string | null;
  hasToken: boolean;
  status: string;
  defaultDestination?: { projectV2Id?: string } | null;
}

/** View store for the "Delivery Connections" admin view (ADO / GitHub Projects v2). */
class DeliveryConnectionsStore {
  rootStore: RootStore;
  loading = false;
  error: string | null = null;

  newName = "";
  newType = "githubProjects";
  newBaseUrl = "";
  newOwner = "";
  newOwnerType = "org";
  newProjectNumber = "";
  newProjectV2Id = "";
  newToken = "";
  saving = false;
  formError: string | null = null;

  // Column sort (client-side over the loaded set; see tableSort.ts).
  sortKey: DeliveryConnectionSortKey = "name";
  sortDir: SortDir = "asc";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get service(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("delivery-connections") ?? this.rootStore.deliveryConnectionsStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  get connections(): DeliveryConnection[] {
    return (this.service?.entities ?? []) as unknown as DeliveryConnection[];
  }

  /** Human label for a connection type — also what the Type column sorts by. */
  typeLabel(connection: DeliveryConnection): string {
    return connection.type === "githubProjects"
      ? "GitHub Projects v2"
      : "Azure DevOps";
  }

  /** The Target column's text — also what that column sorts by. */
  targetLabel(connection: DeliveryConnection): string {
    return connection.type === "githubProjects"
      ? `${connection.owner ?? "?"}/#${connection.projectNumber ?? "?"}`
      : connection.baseUrl || "";
  }

  /** The rows the table renders: the loaded set under the active column sort. */
  get sortedConnections(): DeliveryConnection[] {
    const key = this.sortKey;
    const valueOf = (c: DeliveryConnection): SortValue => {
      switch (key) {
        case "name":
          return textValue(c.name);
        case "type":
          return textValue(this.typeLabel(c));
        case "target":
          return textValue(this.targetLabel(c));
      }
    };
    return sortRows(this.connections, valueOf, this.sortDir);
  }

  /** Column-header click: same column toggles direction, a new one starts A→Z. */
  setSort(key: DeliveryConnectionSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  setField(
    field:
      | "newName"
      | "newType"
      | "newBaseUrl"
      | "newOwner"
      | "newOwnerType"
      | "newProjectNumber"
      | "newProjectV2Id"
      | "newToken",
    value: string
  ) {
    this[field] = value;
  }

  get isGitHub(): boolean {
    return this.newType === "githubProjects";
  }

  async load() {
    this.loading = true;
    this.error = null;
    try {
      // findOrThrow: the ServiceStore resolves null on a failed find, which
      // rendered the "none yet" empty state.
      await findOrThrow(this.service, { query: { $limit: 200, $sort: { name: 1 } } });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = loadErrorMessage(err, "Failed to load delivery connections");
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }
  async reload() {
    await this.load();
  }

  async addConnection(): Promise<void> {
    if (!this.newName.trim()) {
      this.formError = "Name is required.";
      return;
    }
    this.saving = true;
    this.formError = null;
    try {
      const payload: Record<string, unknown> = { name: this.newName.trim(), type: this.newType };
      if (this.isGitHub) {
        payload.baseUrl = this.newBaseUrl.trim() || "https://api.github.com/graphql";
        payload.owner = this.newOwner.trim() || null;
        payload.ownerType = this.newOwnerType;
        payload.projectNumber = this.newProjectNumber.trim() ? Number(this.newProjectNumber) : null;
        payload.authMode = "pat";
        if (this.newProjectV2Id.trim()) {
          payload.defaultDestination = { projectV2Id: this.newProjectV2Id.trim() };
        }
      } else {
        payload.baseUrl = this.newBaseUrl.trim() || null;
      }
      if (this.newToken.trim()) payload.token = this.newToken.trim();
      await this.service?.create(payload as never);
      runInAction(() => {
        this.newName = "";
        this.newOwner = "";
        this.newProjectNumber = "";
        this.newProjectV2Id = "";
        this.newToken = "";
      });
      await this.reload();
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.formError = err instanceof Error ? err.message : "Failed to add connection";
      });
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  /**
   * Report a failed user-initiated action: toast to the user AND a structured
   * `view.action_failed` diagnostic into the retained ring buffer. The delete
   * path below used to end in `console.error` — the connection stayed on
   * screen and the app said nothing at all.
   */
  private reportFailure = createActionReporter(
    () => this.rootStore,
    "DeliveryConnectionsStore",
  );

  async removeConnection(id: string) {
    try {
      await this.service?.remove(id);
      await this.reload();
    } catch (err) {
      this.reportFailure("remove the delivery connection", err, {
        data: { id },
      });
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.loading = false;
    this.error = null;
    this.newName = "";
    this.newType = "githubProjects";
    this.newBaseUrl = "";
    this.newOwner = "";
    this.newOwnerType = "org";
    this.newProjectNumber = "";
    this.newProjectV2Id = "";
    this.newToken = "";
    this.saving = false;
    this.formError = null;
    this.sortKey = "name";
    this.sortDir = "asc";
  }
}

export default DeliveryConnectionsStore;
